import * as Blockly from 'blockly/core';
import 'blockly/javascript';
import 'blockly/python';
import 'blockly/msg/en';
import { parseOpenFoamToJson } from './openfoam/parser';
import { registerJinjaBlocks, generateJinjaToolbox } from './jinja/jinja.blocks';
import { registerOpenFoamBlocks, generateOpenFoamToolbox, updateToolboxWithOpenFoamBlocks } from './openfoam/openfoam.blocks';
import schema from './openfoam/openfoam.schema.json';
import { convertTextToBlocks } from './openfoam/openfoam.adapter.js';

// Configuration for block modules
const blockModules = [
  {
    name: 'openfoam',
    register: (params) => registerOpenFoamBlocks(params.dictName),
    generateToolbox: (params) => generateOpenFoamToolbox(params.dictName),
    updateToolbox: (toolboxXml, params) => updateToolboxWithOpenFoamBlocks(toolboxXml, params.dictName),
    params: { dictName: null }, // Will be set dynamically
  },
  {
    name: 'jinja',
    register: (params) => registerJinjaBlocks(params.varOptions),
    generateToolbox: (params) => generateJinjaToolbox(params.varOptions),
    updateToolbox: (toolboxXml, params) => updateToolboxWithJinjaBlocks(toolboxXml, params.varOptions),
    params: { varOptions: [] },
  },
];

export const initializeBlockly = async (dictName, parameterGroups, fileContent, blocklyDiv) => {
  // Extract dropdown options for Jinja variables
  const extractDropdownOptionsFromParams = (paramGroups) => {
    if (!paramGroups || !Array.isArray(paramGroups)) {
      console.warn('Parameter groups are missing or invalid, returning default options');
      return [['parameter', 'parameter']]; // Default option
    }
    
    const options = paramGroups.flatMap((group) =>
      group.children && Array.isArray(group.children)
        ? group.children
            .map((param) => [param.name, param.name])
            .filter((opt) => opt[0])
        : []
    );
    
    // If no options were extracted, provide a default
    if (options.length === 0) {
      console.warn('No options extracted from parameter groups, using default');
      return [['parameter', 'parameter']];
    }
    
    console.log('Extracted dropdown options:', options);
    return options;
  };

  // Validate OpenFOAM schema
  let dictSchema = null;
  try {
    dictSchema = schema.dictionaries[dictName];
    if (!dictSchema) {
      // Create a default schema for unknown dictionaries
      dictSchema = {
        prefix: dictName.toLowerCase(),
        keywords: {},
        valueTypes: {
          string: {
            type: 'string',
            shadowBlock: 'value_string',
            description: 'String input',
            default: '',
          },
          number: {
            type: 'number',
            shadowBlock: 'value_number',
            description: 'Number input',
            default: 0,
          }
        }
      };
      console.log(`Created default schema for dictionary: ${dictName}`);
    }
    console.log(
      'Schema for:',
      dictName,
      'prefix:',
      dictSchema.prefix,
      'keywords:',
      Object.keys(dictSchema.keywords || {})
    );
  } catch (err) {
    console.error(`Failed to load schema for '${dictName}':`, err);
    // Create a minimal schema for error cases
    dictSchema = {
      prefix: dictName.toLowerCase(),
      keywords: {},
      valueTypes: {
        string: {
          type: 'string',
          shadowBlock: 'value_string',
          description: 'String input',
          default: '',
        }
      }
    };
  }
  const prefix = dictSchema.prefix;

  // Initialize block modules
  const varOptions = extractDropdownOptionsFromParams(parameterGroups);
  console.log('Parameter groups:', parameterGroups);
  console.log('Jinja variable options:', varOptions);

  // Set module parameters
  blockModules.forEach((module) => {
    if (module.name === 'openfoam') {
      module.params.dictName = dictName;
      module.params.schema = dictSchema;  // Pass schema to module
      module.params.filename = dictName;  // Pass filename to module
    } else if (module.name === 'jinja') {
      module.params.varOptions = varOptions.length > 0 ? varOptions : [];
    }
  });

  // Register blocks for all modules
  try {
    blockModules.forEach((module) => {
      module.register(module.params);
      console.log(`${module.name} blocks registered`);
    });
  } catch (err) {
    console.error('Failed to register blocks:', err);
    return null;
  }

  // Initialize toolbox
  let toolboxXml = '<xml>';
  try {
    // Create category structure for better organization
    const dictCategory = document.createElement('category');
    dictCategory.setAttribute('name', dictName || 'OpenFOAM');
    dictCategory.setAttribute('colour', '210');
    
    const jinjaCategory = document.createElement('category');
    jinjaCategory.setAttribute('name', 'Jinja');
    jinjaCategory.setAttribute('colour', '120');
    
    const commonCategory = document.createElement('category');
    commonCategory.setAttribute('name', 'Common');
    commonCategory.setAttribute('colour', '160');
    
    // Parse and add blocks to appropriate categories
    blockModules.forEach((module) => {
      const moduleToolbox = module.generateToolbox(module.params);
      const parser = new DOMParser();
      const moduleDoc = parser.parseFromString(moduleToolbox, 'text/xml');
      
      if (moduleDoc.documentElement.nodeName === 'parsererror') {
        throw new Error(`Failed to parse ${module.name} toolbox XML`);
      }
      
      // Add blocks to appropriate category
      Array.from(moduleDoc.documentElement.children).forEach((child) => {
        if (child.tagName === 'block') {
          const blockType = child.getAttribute('type') || '';
          
          // Determine which category to add the block to
          if (blockType.startsWith(`${dictSchema?.prefix}_`) || 
              blockType.startsWith(`${dictName.toLowerCase()}_`)) {
            // Add dictionary-specific blocks to the dictionary category
            dictCategory.appendChild(child.cloneNode(true));
          } else if (blockType.startsWith('jinja_')) {
            // Add Jinja blocks to the Jinja category
            jinjaCategory.appendChild(child.cloneNode(true));
          } else if (blockType.startsWith('common_') || 
                    blockType.startsWith('value_') || 
                    blockType === 'tuple') {
            // Add common blocks to the Common category
            commonCategory.appendChild(child.cloneNode(true));
          } else {
            // Default to dictionary category for any other blocks
            dictCategory.appendChild(child.cloneNode(true));
          }
        }
      });
    });
    
    // Add categories to toolbox
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString('<xml></xml>', 'text/xml');
    xmlDoc.documentElement.appendChild(dictCategory);
    xmlDoc.documentElement.appendChild(commonCategory);
    xmlDoc.documentElement.appendChild(jinjaCategory);
    
    toolboxXml = xmlDoc.documentElement;
    console.log(`Toolbox XML generated with categories: ${dictName}, Common, Jinja`);
  } catch (err) {
    console.error('Failed to generate categorized toolbox:', err);
    // Fallback to simple toolbox
    try {
      blockModules.forEach((module) => {
        const moduleToolbox = module.generateToolbox(module.params);
        const parser = new DOMParser();
        const moduleDoc = parser.parseFromString(moduleToolbox, 'text/xml');
        if (moduleDoc.documentElement.nodeName === 'parsererror') {
          throw new Error(`Failed to parse ${module.name} toolbox XML`);
        }
        // Append module blocks directly to toolboxXml
        Array.from(moduleDoc.documentElement.children).forEach((child) => {
          if (child.tagName === 'block') {
            toolboxXml += new XMLSerializer().serializeToString(child);
          }
        });
      });
      toolboxXml += '</xml>';
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(toolboxXml, 'text/xml');
      if (xmlDoc.documentElement.nodeName === 'parsererror') {
        throw new Error('Failed to parse combined toolbox XML');
      }
      toolboxXml = xmlDoc.documentElement;
      console.log('Fallback toolbox XML generated');
    } catch (fallbackErr) {
      console.error('Failed to generate fallback toolbox:', fallbackErr);
      const parser = new DOMParser();
      toolboxXml = parser.parseFromString(
        `<xml xmlns="https://developers.google.com/blockly/xml">
          <block type="${prefix}_root"></block>
        </xml>`,
        'text/xml'
      ).documentElement;
      console.warn('Using minimal fallback toolbox XML');
    }
  }

  // Initialize Blockly workspace
  console.log('Initializing Blockly workspace');
  let workspace = null;
  try {
    workspace = Blockly.inject(blocklyDiv, {
      toolbox: toolboxXml,
      scrollbars: true,
      trashcan: true,
      zoom: {
        controls: true,
        wheel: false,
        startScale: 1.0,
        maxScale: 2,
        minScale: 0.7,
        scaleSpeed: 0.8,
      },
      // Enable collapsible toolbox
      collapse: true,
      // Configure toolbox position and behavior for vertical layout
      toolboxPosition: 'start', // 'start' is left, 'end' is right
      horizontalLayout: false, // Ensure vertical layout
      // Configure flyout for vertical display
      maxTrashcanContents: 20,
      // Configure grid
      grid: {
        spacing: 20,
        length: 3,
        colour: '#ccc',
        snap: true
      },
      // Configure zoom controls position
      zoomOptions: {
        controls: true,
        startScale: 1.0,
        maxScale: 3,
        minScale: 0.3,
        scaleSpeed: 1.2,
        pinch: true
      },
      // Improve rendering with vertical-optimized renderer
      renderer: 'geras', // Use the more detailed renderer
      // Force vertical toolbox layout
      theme: Blockly.Themes.Classic,
    });
    console.log('Blockly workspace initialized');
  } catch (err) {
    console.error('Failed to initialize Blockly workspace:', err);
    return null;
  }

  // Add CSS for smooth transitions and narrow toolbox
  const addToolboxStyles = () => {
    const style = document.createElement('style');
    style.textContent = `
      .blocklyToolboxDiv {
        transition: transform 0.3s, opacity 0.3s, width 0.3s;
        width: 60px !important; /* Make even narrower for vertical text */
        overflow-x: hidden;
        display: flex !important;
        flex-direction: column !important; /* Force vertical layout */
      }
      .blocklyTreeRow {
        padding: 8px 4px !important; /* Adjusted padding for vertical text */
        margin-bottom: 2px !important;
        height: 80px !important; /* Increased height for vertical text */
        line-height: 1.2 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        box-sizing: border-box !important;
        writing-mode: vertical-rl !important; /* Make text vertical */
        text-orientation: mixed !important;
      }
      .blocklyTreeLabel {
        font-size: 11px !important;
        white-space: nowrap;
        writing-mode: vertical-rl !important; /* Vertical text */
        text-orientation: mixed !important;
        transform: rotate(180deg) !important; /* Rotate for better readability */
        max-width: none !important;
        max-height: 70px !important;
        display: block !important;
        width: 100% !important;
        text-align: center !important;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      /* Alternative approach for better text display */
      .blocklyTreeRow:hover .blocklyTreeLabel {
        background-color: rgba(255, 255, 255, 0.8);
        border-radius: 2px;
        padding: 2px;
      }
      /* Category styling for vertical layout */
      .blocklyTreeRoot {
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
      }
      .blocklyTreeRoot > div {
        margin-bottom: 2px !important; /* Reduced spacing for vertical layout */
        width: 100% !important;
        display: block !important;
      }
      .blocklyTreeRoot > div > div.blocklyTreeRow {
        font-weight: bold;
        border-radius: 4px;
        padding: 8px 4px !important; /* Adjusted for vertical text */
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        height: 80px !important; /* Increased height for vertical text */
        box-sizing: border-box !important;
        writing-mode: vertical-rl !important;
        text-orientation: mixed !important;
      }
      /* Ensure blocks in categories stack vertically */
      .blocklyTreeRoot .blocklyTreeRow + .blocklyTreeRow {
        margin-top: 2px !important;
      }
      /* Improve flyout appearance for vertical toolbox */
      .blocklyFlyout {
        width: 250px !important; /* Make flyout wider than toolbox */
      }
      .blocklyFlyoutButton {
        fill: #f1f1f1;
      }
      /* Force toolbox contents to display vertically */
      .blocklyToolboxContents {
        display: flex !important;
        flex-direction: column !important;
        height: 100% !important;
      }
      /* Additional styling for compact vertical text */
      .blocklyTreeIcon {
        display: none !important; /* Hide icons to save space */
      }
      .blocklyTreeRow.blocklyTreeSelected {
        background-color: rgba(0, 123, 255, 0.3) !important;
      }
      /* Prevent white background on selection and change text color instead */
      .blocklyTreeRow.blocklyTreeSelected {
        background-color: transparent !important;
        color: #007bff !important; /* Blue text for selected items */
      }
      .blocklyTreeRow.blocklyTreeSelected .blocklyTreeLabel {
        color: #007bff !important; /* Blue text for selected labels */
        font-weight: bold !important;
        background-color: transparent !important;
      }
      /* Override any default Blockly selection styles */
      .blocklyTreeRow:focus,
      .blocklyTreeRow:active,
      .blocklyTreeRow.blocklyTreeRowSelected {
        background-color: transparent !important;
        color: #007bff !important;
      }
      .blocklyTreeRow:focus .blocklyTreeLabel,
      .blocklyTreeRow:active .blocklyTreeLabel,
      .blocklyTreeRow.blocklyTreeRowSelected .blocklyTreeLabel {
        color: #007bff !important;
        font-weight: bold !important;
        background-color: transparent !important;
      }
    `;
    document.head.appendChild(style);
  };

  // Add toolbox toggle and styles
  addToolboxStyles();

  // Utility to create and connect shadow block
  const createShadowBlock = (workspace, shadowType, value, meta) => {
    if (!Blockly.Blocks[shadowType]) {
      console.warn(`Shadow block ${shadowType} not found, using value_string`);
      shadowType = 'value_string';
    }
    const shadow = workspace.newBlock(shadowType);
    shadow.setFieldValue(
      String(value !== undefined ? value : (meta.default || '')),
      'VALUE'
    );
    shadow.setShadow(true);
    shadow.filename = dictName;  // Set filename on block
    shadow.initSvg();
    shadow.render();
    return shadow;
  };

  // Register dynamic block for unknown OpenFOAM keys
  const registerDynamicBlock = (blockType, keyName, meta = { type: 'string', shadowBlock: 'value_string' }) => {
    if (!Blockly.Blocks[blockType]) {
      Blockly.Blocks[blockType] = {
        init: function () {
          this.filename = dictName;  // Set filename on block
          this.appendValueInput('VALUE')
            .setCheck(meta.type === 'boolean' ? 'Boolean' : ['String', 'Number'])
            .appendField(`${keyName}`);
          this.setPreviousStatement(true, null);
          this.setNextStatement(true, null);
          this.setColour(160);
          this.setTooltip(`Dynamic OpenFOAM block for ${keyName}`);
          this.setHelpUrl('');
        },
      };
      console.log(`Registered dynamic OpenFOAM block: ${blockType} with shadow: ${meta.shadowBlock}`);
    }
  };

  // Register dynamic Jinja variable block
  const registerDynamicJinjaVariable = (varName) => {
    const blockType = `jinja_variable_${varName}`;
    if (!Blockly.Blocks[blockType]) {
      Blockly.Blocks[blockType] = {
        init: function () {
          this.appendDummyInput().appendField(`{{ ${varName} }}`);
          this.setOutput(true, 'String');
          this.setColour(100);
          this.setTooltip(`Jinja variable: ${varName}`);
          this.setHelpUrl('');
        },
      };
      console.log(`Registered dynamic Jinja variable block: ${blockType}`);
    }
    return blockType;
  };

  // Build blocks from dictionary
  const buildBlocksFromDict = (dict, prefix, parentConnection) => {
    console.log(`Building blocks from dict with prefix: ${prefix}`);
    let currentConnection = parentConnection;

    for (const key in dict) {
      let blockType = `${prefix}_${key}`;
      let value = dict[key];
      let meta = dictSchema.keywords[key] || { type: 'string', shadowBlock: 'value_string', default: '' };

      // Handle Jinja variables
      if (typeof value === 'string' && value.match(/^{{.*}}$/)) {
        const varName = value.replace(/^{{\s*|\s*}}$/g, '').trim();
        blockType = registerDynamicJinjaVariable(varName);
      } else if (!Blockly.Blocks[blockType]) {
        registerDynamicBlock(blockType, key, meta);
      }

      try {
        const block = workspace.newBlock(blockType);
        block.filename = dictName;  // Set filename on block
        console.log(`Created block: ${blockType}`);

        block.initSvg();
        block.render();

        if (blockType.startsWith('jinja_variable_')) {
          const input = parentConnection && parentConnection.type === Blockly.INPUT_VALUE ? parentConnection : block.getInput('VALUE');
          if (input && block.outputConnection) {
            input.connect(block.outputConnection);
            console.log(`Connected Jinja variable ${blockType} to value input`);
          }
        } else {
          if (currentConnection && block.previousConnection) {
            currentConnection.connect(block.previousConnection);
            console.log(`Connected ${blockType} to parent`);
          }

          if (typeof value === 'string' || typeof value === 'number') {
            const input = block.getInput('VALUE');
            if (input) {
              const shadowType = meta.shadowBlock || 'value_string';
              const shadow = createShadowBlock(workspace, shadowType, value, meta);
              input.connection.connect(shadow.outputConnection);
              console.log(`Connected shadow block ${shadowType} for ${blockType} with value: ${value}`);
            }
          } else if (typeof value === 'object' && !Array.isArray(value)) {
            const nestedBlockType = `${prefix}_${key}_dict`;
            if (!Blockly.Blocks[nestedBlockType]) {
              Blockly.Blocks[nestedBlockType] = {
                init: function () {
                  this.appendDummyInput().appendField(key);
                  this.appendStatementInput('PROPS').setCheck(null);
                  this.setPreviousStatement(true, null);
                  this.setNextStatement(true, null);
                  this.setColour(160);
                  this.setTooltip(`Nested dictionary for ${key}`);
                },
              };
              console.log(`Registered nested dictionary block: ${nestedBlockType}`);
            }
            const nestedBlock = workspace.newBlock(nestedBlockType);
            nestedBlock.initSvg();
            nestedBlock.render();
            if (currentConnection) {
              currentConnection.connect(nestedBlock.previousConnection);
            }
            const nestedInput = nestedBlock.getInput('PROPS').connection;
            buildBlocksFromDict(value, prefix, nestedInput);
            currentConnection = nestedBlock.nextConnection;
          }

          currentConnection = block.nextConnection;
        }
      } catch (err) {
        console.error(`Failed to create or render block ${blockType}:`, err);
      }
    }
  };

  // Load just the root block
  const loadRootBlockOnly = (prefix) => {
    console.log(`Loading only root block for prefix: ${prefix}`);
    workspace.clear();

    try {
      const rootBlockType = `${prefix}_root`;
      if (!Blockly.Blocks[rootBlockType]) {
        console.error(`Root block ${rootBlockType} not defined`);
        return null;
      }
      const root = workspace.newBlock(rootBlockType);
      root.setDeletable(false);
      root.initSvg();
      root.render();
      console.log(`Created and rendered root block: ${rootBlockType}`);
      
      workspace.centerOnBlock(root.id);
      return root;
    } catch (err) {
      console.error('Failed to load root block:', err);
      return null;
    }
  };

  // Initialize blocks
  const initializeBlocks = () => {
    console.log('Workspace ready, initializing blocks');
    workspace.clear();

    try {
      if (fileContent) {
        try {
          // Use the convertTextToBlocks function to create blocks from OpenFOAM text
          const rootBlock = convertTextToBlocks(fileContent, dictName, workspace);
          if (rootBlock) {
            console.log('Successfully converted OpenFOAM text to blocks');
            workspace.centerOnBlock(rootBlock.id);
            return;
          } else {
            console.error('Failed to convert OpenFOAM text to blocks, falling back to root block only');
          }
        } catch (err) {
          console.error('Failed to parse fileContent:', err);
        }
      }
      
      // If we get here, either there was no file content or parsing failed
      // Just create the root block without any initial blocks
      loadRootBlockOnly(prefix);
      
    } catch (err) {
      console.error('Failed to initialize blocks:', err);
    }
  };

  // Schedule block initialization
  console.log('Scheduling block initialization');
  setTimeout(() => {
    console.log('Executing block initialization');
    initializeBlocks();
  }, 500);

  // Add resize handler for responsive workspace
  const onresize = function() {
    // Get the blocklyArea (parent of blocklyDiv)
    const blocklyArea = blocklyDiv.parentNode;
    if (!blocklyArea) return;
    
    // Compute the absolute coordinates and dimensions of blocklyArea
    let x = 0;
    let y = 0;
    let currentElement = blocklyArea;
    
    do {
      x += currentElement.offsetLeft;
      y += currentElement.offsetTop;
      currentElement = currentElement.offsetParent;
    } while (currentElement);
    
    // Position blocklyDiv over blocklyArea
    blocklyDiv.style.left = x + 'px';
    blocklyDiv.style.top = y + 'px';
    blocklyDiv.style.width = blocklyArea.offsetWidth + 'px';
    blocklyDiv.style.height = blocklyArea.offsetHeight + 'px';
    
    // Resize the Blockly workspace
    Blockly.svgResize(workspace);
  };

  // Add resize event listener
  window.addEventListener('resize', onresize, false);

  // Initial resize
  onresize();

  // Store the resize handler in a property that can be accessed by the component
  workspace.resizeHandler = onresize;

  return workspace;
};

// Utility to update toolbox with Jinja blocks
function updateToolboxWithJinjaBlocks(toolboxXml, varOptions) {
  let toolboxDoc;
  const parser = new DOMParser();

  // Parse input toolbox XML
  try {
    toolboxDoc = parser.parseFromString(toolboxXml, 'text/xml');
    if (toolboxDoc.documentElement.nodeName === 'parsererror') {
      throw new Error('Invalid input toolbox XML');
    }
  } catch (err) {
    console.error('Failed to parse input toolbox XML:', err);
    toolboxDoc = parser.parseFromString('<xml></xml>', 'text/xml');
  }

  // Add Jinja variable blocks
  varOptions.forEach(([varName]) => {
    const block = toolboxDoc.createElement('block');
    block.setAttribute('type', `jinja_variable_${varName}`);
    toolboxDoc.documentElement.appendChild(block);
  });

  // Serialize updated toolbox XML
  const updatedXml = new XMLSerializer().serializeToString(toolboxDoc);
  console.log('Updated toolbox XML with Jinja blocks:', updatedXml);
  return updatedXml;
}
